import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

const NETWORKS = ["MTN", "AirtelTigo", "Telecel"];

const getAgentCollection = (network) => {
  switch (network) {
    case "MTN":
      return "agent_packages_MTN";
    case "AirtelTigo":
      return "agent_packages_AirtelTigo";
    case "Telecel":
      return "agent_packages_Telecel";
    default:
      return "agent_packages_MTN";
  }
};

export default function AdminAgentPrices() {
  const navigate = useNavigate();

  const [activeNetwork, setActiveNetwork] = useState("MTN");
  const [packages, setPackages] = useState([]);

  const [form, setForm] = useState({
    id: "",
    price: "",
    description: "",
    outOfStock: false,
  });

  /* LOAD AGENT PACKAGES ONLY */
  useEffect(() => {
    async function load() {
      const snap = await getDocs(
        collection(db, getAgentCollection(activeNetwork))
      );

      setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      resetForm();
    }

    load();
  }, [activeNetwork]);

  const resetForm = () => {
    setForm({
      id: "",
      price: "",
      description: "",
      outOfStock: false,
    });
  };

  /* SAVE AGENT PACKAGE */
  const savePackage = async () => {
    if (!form.id || !form.price) {
      alert("Package ID and price required");
      return;
    }

    await setDoc(
      doc(db, getAgentCollection(activeNetwork), form.id),
      {
        price: Number(form.price),
        description: form.description,
        outOfStock: form.outOfStock,
        network: activeNetwork,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    resetForm();
    reload();
  };

  const reload = async () => {
    const snap = await getDocs(
      collection(db, getAgentCollection(activeNetwork))
    );
    setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete agent package?")) return;
    await deleteDoc(doc(db, getAgentCollection(activeNetwork), id));
    reload();
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* HEADER */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-extrabold text-sky-400">
            Agent Packages
          </h1>
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="px-4 py-2 bg-gray-600 rounded"
          >
            Back
          </button>
        </div>

        {/* NETWORK SELECT */}
        <div className="flex gap-3">
          {NETWORKS.map((n) => (
            <button
              key={n}
              onClick={() => setActiveNetwork(n)}
              className={`px-4 py-2 rounded-xl font-bold ${
                activeNetwork === n
                  ? "bg-sky-600"
                  : "bg-slate-700"
              }`}
            >
              {n}
            </button>
          ))}
        </div>

        {/* ADD FORM */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h2 className="font-bold mb-4">
            Add Agent {activeNetwork} Package
          </h2>

          <div className="grid md:grid-cols-4 gap-3">
            <input
              placeholder="Package ID (e.g. 10GB)"
              value={form.id}
              onChange={(e) =>
                setForm({ ...form, id: e.target.value })
              }
              className="p-3 rounded bg-slate-700"
            />

            <input
              type="number"
              placeholder="Agent Price"
              value={form.price}
              onChange={(e) =>
                setForm({ ...form, price: e.target.value })
              }
              className="p-3 rounded bg-slate-700"
            />

            <input
              placeholder="Description (optional)"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="p-3 rounded bg-slate-700"
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.outOfStock}
                onChange={(e) =>
                  setForm({
                    ...form,
                    outOfStock: e.target.checked,
                  })
                }
              />
              Out of Stock
            </label>
          </div>

          <button
            onClick={savePackage}
            className="mt-4 px-6 py-3 bg-green-600 rounded-xl font-bold"
          >
            Save Agent Package
          </button>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-800">
              <tr>
                <th className="p-3">Package</th>
                <th className="p-3">Price</th>
                <th className="p-3">Status</th>
                <th className="p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.id} className="border-t text-center">
                  <td className="p-3 font-bold">{p.id}</td>
                  <td className="p-3">GHS {p.price}</td>
                  <td className="p-3">
                    {p.outOfStock ? (
                      <span className="text-red-400">OUT</span>
                    ) : (
                      <span className="text-green-400">
                        ACTIVE
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => deletePackage(p.id)}
                      className="px-3 py-1 bg-red-600 rounded font-bold"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {packages.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="p-6 text-center text-gray-400"
                  >
                    No agent packages added yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
